/* eslint-disable sonarjs/no-duplicate-string */
import { coreApi } from '@modules/core/api';
import { useQuery } from '@tanstack/react-query';
import {
  FeatureFlagsResponse,
  FeatureFlags,
  DEFAULT_ACTIVE_FEATURE_FLAGS,
  AMPLITUDE_FEATURE_FLAGS,
  FeatureConfig,
  FeatureSetting, Path,
} from '@modules/core/api/types';
import { useGlobalStore } from '@modules/core/store';
import { useCallback, useMemo } from 'react';

const defaultFlags = Object.fromEntries(
  DEFAULT_ACTIVE_FEATURE_FLAGS.map((featureFlag) => [featureFlag, true])
) as FeatureFlags;

const onQueryResponse = (featureFlags: FeatureFlagsResponse): FeatureFlags => Object.fromEntries(
  Object.entries(featureFlags).map(([key, { key: flagKey }]) => [key, flagKey === 'on' || flagKey === 'treatment'])
) as FeatureFlags;

const isFeatureActive = <T = FeatureConfig>(flagName: Path<T, FeatureSetting>, featureFlagConfiguration: T): boolean => {
  const featureConfig = flagName.split('.')
    .reduce(
      (config, path) => config?.[path],
      featureFlagConfiguration as Record<string, any> | undefined
    ) as FeatureSetting | undefined;

  if (!featureConfig?.enabled) {
    return false;
  }
  if (!featureConfig.dependsOn || featureConfig.dependsOn.length === 0) {
    return true;
  }
  return featureConfig.dependsOn.every(
    (dependency) => isFeatureActive(dependency as Path<T, FeatureSetting>, featureFlagConfiguration)
  );
};

const useFeatureFlags = () => {
  const { user, canFetchFeatureConfiguration } = useGlobalStore();
  const featureFlagsUserId = user?.id ?? '';

  const {
    data: fetchedData, isFetched, error, refetch, isRefetching, failureCount,
  } = useQuery({
    queryKey: ['featureFlags', featureFlagsUserId],
    queryFn: async () => coreApi.getFeatureFlags(featureFlagsUserId),
    retry: 2,
    enabled: !!featureFlagsUserId && canFetchFeatureConfiguration,
    // One hour
    staleTime: 1000 * 60 * 60,
    select: onQueryResponse,
  });

  const data = !isFetched || error ? defaultFlags : fetchedData;

  const featureConfiguration: FeatureConfig = useMemo(() => ({
    system: { maintenanceMode: { enabled: data?.[AMPLITUDE_FEATURE_FLAGS.CLIENT_HUB_MAINTENANCE_MODE] } },
    account: { phoneNumberEditable: { enabled: data?.[AMPLITUDE_FEATURE_FLAGS.CLIENT_HUB_BROKER_PHONE_NUMBER_EDITABLE] } },
    announcements: { referAndEarn: { enabled: data?.[AMPLITUDE_FEATURE_FLAGS.REFER_AND_EARN_ANNOUNCEMENT] } },
    banks: {
      module: { enabled: data?.[AMPLITUDE_FEATURE_FLAGS.CLIENT_HUB_BANKS_MODULE] },
      updates: {
        enabled: true,
        dependsOn: ['banks.module'],
      },
      products: {
        enabled: true,
        dependsOn: ['banks.module'],
      },
      documents: {
        enabled: true,
        dependsOn: ['banks.module'],
      },
    },
    clients: {
      module: { enabled: true },
      companyClients: {
        enabled: data?.[AMPLITUDE_FEATURE_FLAGS.CLIENT_HUB_COMPANY_CLIENTS],
        dependsOn: ['clients.module'],
      },
    },
    credits: {
      module: { enabled: data?.[AMPLITUDE_FEATURE_FLAGS.CLIENT_HUB_CREDITS_MODULE] },
      paymentGateway: {
        enabled: data?.[AMPLITUDE_FEATURE_FLAGS.CLIENT_HUB_PAYMENT_GATEWAY],
        dependsOn: ['credits.module'],
      },
      blockLowCredits: {
        enabled: data?.[AMPLITUDE_FEATURE_FLAGS.CLIENT_HUB_LOW_CREDITS_FEATURE_BLOCKING],
        dependsOn: ['credits.module'],
      },
      expirationBanner: {
        enabled: data?.[AMPLITUDE_FEATURE_FLAGS.CLIENT_HUB_CREDIT_EXPIRATION_BANNER],
        dependsOn: ['credits.module'],
      },
    },
    dashboards: { module: { enabled: data?.[AMPLITUDE_FEATURE_FLAGS.CLIENT_HUB_DASHBOARDS_MODULE] } },
    opportunities: {
      module: { enabled: true },
      caseCreation: {
        enabled: data?.[AMPLITUDE_FEATURE_FLAGS.CLIENT_HUB_CREATE_CASE],
        dependsOn: ['opportunities.module'],
      },
      directToBankSubmission: {
        enabled: data?.[AMPLITUDE_FEATURE_FLAGS.CLIENT_HUB_CASE_SUBMISSION_DIRECT_TO_BANK],
        dependsOn: ['opportunities.module'],
      },
      vaultAutoSave: {
        enabled: data?.[AMPLITUDE_FEATURE_FLAGS.CLIENT_VAULT_AUTO_SAVE],
        dependsOn: ['opportunities.module'],
      },
      esignature: {
        enabled: data?.[AMPLITUDE_FEATURE_FLAGS.CLIENT_HUB_ESIGNATURE_FLOW],
        dependsOn: ['opportunities.module'],
      },
      mortgageUsage: {
        enabled: data?.[AMPLITUDE_FEATURE_FLAGS.CLIENT_HUB_MORTGAGE_USAGE],
        dependsOn: ['opportunities.module'],
      },
    },
    proposals: {
      module: { enabled: data?.[AMPLITUDE_FEATURE_FLAGS.CLIENT_HUB_PROPOSALS_MODULE] },
      baytecaMVP: {
        enabled: data?.[AMPLITUDE_FEATURE_FLAGS.CLIENT_HUB_PROPOSALS_BAYTECA_MVP],
        dependsOn: ['proposals.module'],
      },
      newPCSCreditsFlow: {
        enabled: data?.[AMPLITUDE_FEATURE_FLAGS.CLIENT_HUB_NEW_PCS_CREDITS_FLOW],
        dependsOn: ['proposals.module'],
      },
      showFeeFinancingColumn: {
        enabled: data?.[AMPLITUDE_FEATURE_FLAGS.CLIENT_HUB_BANK_PRODUCTS_SHOW_FEE_FINANCING_COLUMN],
        dependsOn: ['proposals.module'],
      },
    },
    payouts: { module: { enabled: data?.[AMPLITUDE_FEATURE_FLAGS.CLIENT_HUB_PAYOUTS_MODULE] } },
    referAndEarn: {
      module: { enabled: data?.[AMPLITUDE_FEATURE_FLAGS.CLIENT_HUB_ENABLE_REFER_AND_EARN] },
      listTable: { enabled: data?.[AMPLITUDE_FEATURE_FLAGS.CLIENT_HUB_REFER_AND_EARN_LIST_TABLE] },
    },
    tutorials: { module: { enabled: data?.[AMPLITUDE_FEATURE_FLAGS.CLIENT_HUB_TUTORIALS_MODULE] } },
  }), [data]);

  return {
    data,
    error,
    refetch,
    isRefetching,
    featureFlags: data,
    isFeatureFlagsLoading: (!isFetched || !fetchedData) && failureCount < 2,
    isFeatureActive: useCallback(
      (featureName: Path<typeof featureConfiguration, FeatureSetting>) => isFeatureActive(featureName, featureConfiguration),
      [featureConfiguration]
    ),
  };
};

export default useFeatureFlags;
